import apiClient from './apiClient';
import { SUPPORT_CHAT_CONFIG } from '../config/supportChatConfig';
import {
  EMPTY_SESSION,
  type AssistantContext,
  type AssistantMessage,
  type AssistantReply,
  type AssistantSession,
  type AssistantTurnResult,
} from '../ai/supportAssistantTypes';
import { processAssistantMessage } from '../ai/supportAssistantOrchestrator';
import { wrapTurn } from '../ai/supportAssistantData';
import { isSocOperationalIntent } from '../ai/supportAssistantIntent';

type RemoteChatResponse = {
  reply?: string;
  content?: string;
  actions?: AssistantReply['actions'];
  rich?: AssistantReply['rich'];
  suggestions?: AssistantReply['suggestions'];
  delegateToLocal?: boolean;
  session?: AssistantSession;
};

export type AssistantResponse = AssistantTurnResult;

async function tryBackendRag(
  userText: string,
  history: AssistantMessage[],
  ctx: AssistantContext,
  session: AssistantSession,
): Promise<AssistantTurnResult | null> {
  try {
    const payload = {
      message: userText,
      history: history.slice(-12).map((m) => ({ role: m.role, content: m.content })),
      context: ctx,
      session,
    };

    const res = await apiClient.post<RemoteChatResponse>(
      SUPPORT_CHAT_CONFIG.apiPath,
      payload,
      { timeout: SUPPORT_CHAT_CONFIG.requestTimeoutMs },
    );
    const data = res.data;

    if (data?.delegateToLocal) return null;

    const content = String(data?.reply ?? data?.content ?? '').trim();
    if (!content) return null;

    return {
      reply: {
        content,
        actions: data?.actions,
        rich: data?.rich,
        suggestions: data?.suggestions,
        source: 'ai',
      },
      session: data?.session ?? session ?? EMPTY_SESSION,
    };
  } catch {
    return null;
  }
}

function shouldStayLocal(userText: string, session: AssistantSession): boolean {
  const trimmed = userText.trim();
  if (session.flow !== 'idle') return true;
  return (
    /^pick:(service|address|order):\d+$/i.test(trimmed) ||
    /^(yes|no|confirm|ok|okay|more|next|skip|default)$/i.test(trimmed)
  );
}

/**
 * Fresh messages go to backend LangChain/RAG first for understanding.
 * Backend delegates booking/payment/order/catalog actions back to deterministic local flows.
 */
export async function askSupportAssistant(
  userText: string,
  history: AssistantMessage[],
  ctx: AssistantContext,
  session: AssistantSession = EMPTY_SESSION,
): Promise<AssistantTurnResult> {
  if (shouldStayLocal(userText, session)) {
    return processAssistantMessage(userText, history, ctx, session);
  }

  if (SUPPORT_CHAT_CONFIG.ragEnabled) {
    const ragResult = await tryBackendRag(userText, history, ctx, session);
    if (isSocOperationalIntent(userText)) {
      return processAssistantMessage(userText, history, ctx, session);
    }
    if (ragResult) return wrapTurn(ragResult);
  }

  return processAssistantMessage(userText, history, ctx, session);
}
